import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import { Button, Flex, H2, Input, SelectDropdown, Span, ToolTip } from '../../lib/components'
import { Pagination } from '../../lib/components/Pagination'
import { Queries, SearchParams } from '../../lib/types'
import { SM } from '../../lib/utils/constants'
import { getTimestampWithTimezone, trimText } from '../../lib/utils/helpers'
import { useBreakpoint } from '../../lib/utils/hooks'
import { queriesFilter, queriesSort } from '../../lib/utils/message'
import { queriesServices } from '../../services'
import './queries-list.scss'

const QueriesList = () => {
  const { breakpoint } = useBreakpoint()
  const [queries, setQueries] = useState<Queries[]>()
  const [searchParams, setSearchParams] = useState<SearchParams>({
    limit: '10',
    offset: '0',
    filterBy: '',
    sortBy: '',
    keyword: '',
  })
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 10,
    count: 5,
    offset: 0,
  })
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    fetchQueriesList()
  }, [currentPage, searchParams])

  const fetchQueriesList = async () => {
    const response: any = await queriesServices.searchQueries(searchParams)
    if (response && response.data && response.status === 200) {
      setQueries(response.data)
      setPagination(response.pagination)
    }
  }

  const handleSearchParams = async ({ name, value }: { name: string; value: string }) => {
    const { limit } = pagination
    setSearchParams({
      ...searchParams,
      offset: '0',
      limit: limit.toString(),
      [name]: value,
    })
    setCurrentPage(1)
    setPagination({ ...pagination, offset: 0 })
  }

  const resetSearchParams = () => {
    setSearchParams({
      limit: '10',
      offset: '0',
      keyword: '',
      filterBy: '',
      sortBy: '',
    })
  }

  const handleCurrentPage = (currentPage: number) => {
    const { limit } = pagination
    setCurrentPage(currentPage)
    setSearchParams({
      ...searchParams,
      offset: `${(currentPage - 1) * limit}`,
    })
    setPagination({ ...pagination, offset: (currentPage - 1) * limit })
  }

  return (
    <Flex variant='column-center'>
      <H2 classes={['mb-48']} text='Inquiries' />
      <div className='inquiries-toolbar-layout gap-12 mb-16 mt-16'>
        <Input
          name='keyword'
          value={searchParams.keyword || ''}
          handleChange={(e) => handleSearchParams({ name: e.target.name, value: e.target.value })}
          placeholder='Search'
        />
        <SelectDropdown
          name='filterBy'
          id='filterId'
          formId=''
          list={queriesFilter}
          value={searchParams.filterBy}
          placeholder={'Filter'}
          handleChange={(val) => handleSearchParams({ name: 'filterBy', value: val })}
          dataTestId='queries-filter-list-dropdown'
        />
        <div className='sort-reset'>
          <SelectDropdown
            name='sortBy'
            id='sortById'
            formId=''
            list={queriesSort}
            value={searchParams.sortBy}
            placeholder={'Sort'}
            handleChange={(val) => handleSearchParams({ name: 'sortBy', value: val })}
            dataTestId='queries-sort-list-dropdown'
          />
          <Button
            handleClick={() => resetSearchParams()}
            classes={['reset-btn']}
            variant='secondary-btn'
            text='Reset'
          />
        </div>
      </div>
      {queries && queries.length ? (
        <div className='w-100'>
          <Flex
            variant='row'
            classes={['mb-24', 'table_scroll_horizontal']}
            dataTestId='queries-table-container'
          >
            <table className='styled-table'>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Subject</th>
                  <th>Message</th>
                  <th>Received on</th>
                  <th>Category (Clients)</th>
                </tr>
              </thead>
              <tbody>
                {queries.map((query) => {
                  return (
                    <tr key={uuidv4()}>
                      <td>{query.name}</td>
                      <td>{query.email}</td>
                      <td>{query.subject}</td>
                      <td>
                        {
                          <ToolTip content={query.message}>
                            <span>{trimText(query.message)}</span>
                          </ToolTip>
                        }
                      </td>
                      <td>
                        {getTimestampWithTimezone(
                          new Date(query.created_at),
                          'Do MMM YYYY HH:mm:ss zzz',
                        )}
                      </td>
                      <td>{query.assistance_category}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </Flex>
          {breakpoint && breakpoint === SM
            ? null
            : queries &&
              queries.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  handleCurrentPage={handleCurrentPage}
                  pagination={pagination}
                />
              )}
        </div>
      ) : (
        <Flex variant={'column-center'} classes={['mt-12']}>
          <Span classes={['center']} variant={'thin'} text='No Inquiries Found' />
        </Flex>
      )}
    </Flex>
  )
}

export { QueriesList }
